import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { triggerCsvDownload, dateStamp } from '../../services/downloadHelpers';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AssessmentFilters from './components/AssessmentFilters';
import AssessmentTable from './components/AssessmentTable';
import AssessmentDetailPanel from './components/AssessmentDetailPanel';
import AssessmentStats from './components/AssessmentStats';
import { useTenant } from '../../context/TenantContext';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../utils/format';
import { buildAssessments } from '../../data/derivedAssessments';

const JointAssessments = () => {
  const { tenant, isTransitioning } = useTenant();
  const { t } = useTranslation();
  const { num, pct } = useFormatters();
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Recompute assessments whenever the active tenant changes
  useEffect(() => {
    setIsLoading(true);
    const built = buildAssessments(tenant);
    // Brief delay so the tenant transition feels intentional
    const timer = setTimeout(() => {
      setAssessments(built);
      setFilteredAssessments(built);
      setSelectedAssessment(null);
      setShowDetailPanel(false);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [tenant.id]);


  const handleFiltersChange = (filters) => {
    let filtered = [...assessments];

    if (filters?.status) {
      filtered = filtered?.filter(assessment => assessment?.status === filters?.status);
    }

    if (filters?.department) {
      filtered = filtered?.filter(assessment => 
        assessment?.department?.toLowerCase() === filters?.department
      );
    }

    if (filters?.priority) {
      filtered = filtered?.filter(assessment => assessment?.priority === filters?.priority);
    }

    if (filters?.assignee) {
      filtered = filtered?.filter(assessment => 
        assessment?.assignee?.toLowerCase()?.replace(' ', '-') === filters?.assignee
      );
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter(assessment => 
        new Date(assessment.triggerDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(assessment => 
        new Date(assessment.triggerDate) <= new Date(filters.dateTo)
      );
    }

    if (filters?.gapThreshold) {
      filtered = filtered?.filter(assessment => 
        parseFloat(assessment?.gapPercentage) >= parseFloat(filters?.gapThreshold)
      );
    }

    setFilteredAssessments(filtered);
  };

  const handleClearFilters = () => {
    setFilteredAssessments(assessments);
  };

  const handleAssessmentSelect = (assessment) => {
    setSelectedAssessment(assessment);
    setShowDetailPanel(true);
  };

  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false);
    setSelectedAssessment(null);
  };

  const handleStatusUpdate = (assessmentId, newStatus) => {
    const updatedAssessments = assessments?.map(assessment =>
      assessment?.id === assessmentId
        ? { ...assessment, status: newStatus }
        : assessment
    );
    setAssessments(updatedAssessments);
    setFilteredAssessments(updatedAssessments);

    if (selectedAssessment && selectedAssessment?.id === assessmentId) {
      setSelectedAssessment({ ...selectedAssessment, status: newStatus });
    }
  };

  const handleCreateAssessment = () => {
    // Navigate to create assessment form or open modal
    console.log('Create new assessment');
  };

  const handleExportData = () => {
    const rows = (filteredAssessments && filteredAssessments.length > 0 ? filteredAssessments : assessments).map((a) => ({
      id: a?.id,
      title: a?.title,
      department: a?.department,
      gap: a?.gap,
      status: a?.status,
      priority: a?.priority,
      assignee: a?.assignee,
      dueDate: a?.dueDate,
    }));
    triggerCsvDownload(`ClearEqual_JointAssessments_${dateStamp()}.csv`, rows);
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/analytics-dashboard' },
    { label: 'Joint Assessments', path: '/joint-assessments' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="text-muted-foreground">{t('common.loading')}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-2 gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('assessments.title')}
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
                  Art. 10
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                {tenant.name}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('assessments.subtitle')}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportData}
              >
                {t('common.export')}
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleCreateAssessment}
              >
                {t('assessments.newAssessment')}
              </Button>
            </div>
          </div>

          {/* Tenant context strip */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-4 mb-6 border-b border-border text-xs">
            <div className="flex items-center gap-1.5">
              <Icon name="Building2" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('dashboard.meta.industry')}</span>
              <span className="font-medium text-foreground">{tenant.industry}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Users" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('assessments.metaWorkers')}</span>
              <span className="font-medium text-foreground tabular-nums">
                {num(tenant.employees)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="TrendingUp" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('assessments.metaMeanGap')}</span>
              <span className={`font-medium tabular-nums ${tenant.metrics.meanPayGap > 5 ? 'text-destructive' : 'text-foreground'}`}>
                {pct(tenant.metrics.meanPayGap)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              <span className="text-muted-foreground">{t('dashboard.meta.hrisSync')}</span>
              <span className="font-medium text-foreground">{t('common.live')}</span>
            </div>
          </div>

          {/* Content fades during tenant transitions */}
          <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-40' : 'opacity-100'}`}>
            {/* Statistics */}
            <AssessmentStats assessments={filteredAssessments} />

            {/* Filters */}
            <AssessmentFilters
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
            />

            {/* Assessment Table */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">
                  {t('assessments.queue', { count: filteredAssessments?.length })}
                </h2>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Info" size={16} />
                  <span>{t('assessments.clickHint')}</span>
                </div>
              </div>
              <AssessmentTable
                assessments={filteredAssessments}
                onAssessmentSelect={handleAssessmentSelect}
                selectedAssessment={selectedAssessment}
            />
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">{t('jointAssessments.page.quickActions')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                iconName="FileCheck"
                iconPosition="left"
              >
                <div className="text-left">
                  <div className="font-medium">{t('jointAssessments.page.bulkStatusUpdate')}</div>
                  <div className="text-sm text-muted-foreground">{t('jointAssessments.page.bulkStatusUpdateBody')}</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                iconName="Calendar"
                iconPosition="left"
              >
                <div className="text-left">
                  <div className="font-medium">{t('jointAssessments.page.scheduleReview')}</div>
                  <div className="text-sm text-muted-foreground">{t('jointAssessments.page.scheduleReviewBody')}</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                iconName="BarChart3"
                iconPosition="left"
              >
                <div className="text-left">
                  <div className="font-medium">{t('jointAssessments.page.analyticsReport')}</div>
                  <div className="text-sm text-muted-foreground">{t('jointAssessments.page.analyticsReportBody')}</div>
                </div>
              </Button>
            </div>
          </div>
          </div>
        </div>
      </main>
      {/* Assessment Detail Panel */}
      {showDetailPanel && (
        <AssessmentDetailPanel
          assessment={selectedAssessment}
          onClose={handleCloseDetailPanel}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default JointAssessments;